<template>
  <ProductCardTile
    data-testid="complementaryProductCart"
    :link="cardProduct.link"
    :image="cardProduct.image"
    :title="cardProduct.name"
    :full-product-price="cardProduct.fullProductPrice"
    :energy-rating="energyRating"
    :discounted-price="cardProduct.discountedPrice"
    :is-discounted="cardProduct.isDiscounted"
    :last30-days-price="cardProduct.last30DaysPrice"
    :rating="cardProduct.rating"
    :reviews-count="cardProduct.reviewsCount"
    :is-checked="isChecked"
    :tags="cardProduct.tags"
    @check-box-clicked="handleCheckBoxClick"
    @product-clicked="hideComplementarySidebar"
  />
</template>

<script lang="ts">

import { productGetters } from '~/utils/ct';
import { LINK_TYPE } from '~~/types/components/Link/LinkType';
import { SIMPLIFIED_CARD } from '~~/constants/productCard';
import { ENERGY_RATING_SIZE } from '~~/constants/energyRating';

import type { ProductVariantExtended } from '~~/types/product/ProductVariantExtended';
import { getEnergyRating } from '~~/helpers/product/energyRating';
import { isBWareProduct } from '~~/helpers/product/productCondition';
import { PRODUCT_TAG } from '~~/types/product/attribute/ProductTag';

export default defineComponent({
  name: 'ComplementaryProductCart',

  props: {
    product: {
      type: Object as PropType<ProductVariantExtended>,
      required: true,
    },
    isAlwaysChecked: {
      type: Boolean,
      default: false,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['product-selection-changed'],
  setup(props, { emit }) {

    const { isComplementarySidebarOpen, toggleComplementarySidebar } = useUiState();
    const { languageAndCountry } = useLocalization();
    const { getPriceInformation } = useProductPrice();

    const { product } = toRefs(props);

    const cardProduct = computed(() => {

      const { fullProductPrice, discountedPrice, isDiscounted, last30DaysPrice } =
        getPriceInformation(product);
      const name = product.value?._name;
      const image = product.value?.images?.[0]?.url;
      const link = `/${product.value._slug}`;
      const reviewsCount = productGetters.getTotalReviews(product.value);
      const rating = productGetters.getAverageRating(product.value);
      const tags = isBWareProduct(product.value.sku)
        ? [PRODUCT_TAG.Bware]
        : undefined;

      return {
        name,
        image,
        fullProductPrice: fullProductPrice.value,
        discountedPrice: discountedPrice.value,
        isDiscounted: isDiscounted.value,
        last30DaysPrice: last30DaysPrice.value,
        link,
        rating,
        reviewsCount,
        tags,
      };
    
    });

    const energyRating = computed(() => {

      return getEnergyRating({ product: product.value, languageAndCountry: languageAndCountry.value });
    
    });

    const handleCheckBoxClick = () => {

      if (props.isAlwaysChecked) {

        return;

      }
      emit('product-selection-changed', {
        sku: props.product.sku,
        product: props.product,
        currentlySelected: props.isSelected,
      });
    
    };

    const hideComplementarySidebar = () => {

      isComplementarySidebarOpen.value && toggleComplementarySidebar();
    
    };

    const isChecked = computed(() => props.isAlwaysChecked || props.isSelected);

    return {
      isChecked,
      handleCheckBoxClick,
      LINK_TYPE,
      SIMPLIFIED_CARD,
      ENERGY_RATING_SIZE,
      cardProduct,
      energyRating,
      hideComplementarySidebar,
    };
  
  },
});

</script>
